import { RiskContextItem, ScreeningCaseContext, ScreeningContextInput } from '@nk/data-entities-lib';

export interface PepData {
  url?: string | null;
  name?: string;
  identifier?: string;
  political_positions?: string[];
  country_codes?: string[];
  political_parties?: string[];
  listing_started_utc?: string | null;
  listing_ended_utc?: string | null;
  fields?: Array<{ tag?: string | null; name?: string | null; value?: string | null }>;
  [key: string]: unknown;
}

export interface SanctionData {
  url?: string | null;
  name?: string;
  identifier?: string;
  country_codes?: string[];
  listing_started_utc?: string | null;
  listing_ended_utc?: string | null;
  related_urls?: string[];
  aml_types?: string[];
  fields?: Array<{ tag?: string | null; name?: string | null; value?: string | null }>;
  [key: string]: unknown;
}

export interface AssociateData {
  name: string;
  type: string;
}

export interface MediaData {
  url?: string | null;
  title?: string | null;
  snippet?: string | null;
  identifier?: string | null;
  publishing_date?: string | null;
}

export interface ListData {
  url?: string | null;
  name?: string;
  identifier?: string;
  country_codes?: string[];
  aml_types?: string[];
  listing_started_utc?: string | null;
  listing_ended_utc?: string | null;
  fields?: Array<{ tag?: string | null; name?: string | null; value?: string | null }>;
  [key: string]: unknown;
}

export interface ParsedRiskContext {
  match_name: string;
  match_score: number;
  comply_entity_type: string;
  amlTypes: string[];
  datesOfBirth: Array<{ value: string; source?: string }>;
  peps: PepData[];
  sanctions: SanctionData[];
  associates: AssociateData[];
  media: MediaData[];
  lists: ListData[];
  names: Array<{ name: string; type?: string }>;
}

/**
 * Type guard to detect new screening context format.
 * New format has 'identifier' and 'case_identifier' at the top level.
 */
export function isNewScreeningContext(context: ScreeningContextInput): context is ScreeningCaseContext {
  return 'case_identifier' in context && 'identifier' in context;
}

/**
 * Parse a single risk context item (works for both old RiskContextItem and new ScreeningCaseContext).
 */
export function parseRiskContext(risk: RiskContextItem | ScreeningCaseContext): ParsedRiskContext {
  return {
    match_name: risk.match_name,
    match_score: risk.match_score,
    comply_entity_type: risk.comply_entity_type,
    amlTypes: (risk.risk_indicators_context?.['aml_types'] as string[]) || [],
    datesOfBirth: (risk.risk_profile_context?.['dates_of_birth'] as Array<{ value: string; source?: string }>) || [],
    peps: (risk.risk_indicators_context?.['peps'] as PepData[]) || [],
    sanctions: (risk.risk_indicators_context?.['sanctions'] as SanctionData[]) || [],
    associates: (risk.risk_profile_context?.['associates'] as AssociateData[]) || [],
    media: (risk.risk_indicators_context?.['media'] as MediaData[]) || [],
    lists: (risk.risk_indicators_context?.['lists'] as ListData[]) || [],
    names: (risk.risk_profile_context?.['names'] as Array<{ name: string; type?: string }>) || [],
  };
}

/**
 * Normalize screening context input to an array of ParsedRiskContext.
 * Handles both old (SupplierCaseContextLegacy) and new (ScreeningCaseContext) formats.
 */
export function normalizeScreeningContext(context: ScreeningContextInput | null | undefined): ParsedRiskContext[] {
  if (!context) {
    return [];
  }

  if (isNewScreeningContext(context)) {
    // New format - single item, wrap in array
    return [parseRiskContext(context)];
  }

  // Old format - extract from risks_context array
  if (!context.risks_context) {
    return [];
  }
  return context.risks_context.map((risk) => parseRiskContext(risk));
}

/**
 * Get the count of risk context items.
 * New format always returns 1, old format returns the length of risks_context array.
 */
export function getScreeningContextRiskCount(context: ScreeningContextInput | null | undefined): number {
  if (!context) {
    return 0;
  }

  if (isNewScreeningContext(context)) {
    // New format - always a single item
    return 1;
  }

  // Old format - count from risks_context array
  return context.risks_context?.length ?? 0;
}
